import axios from 'axios'
import { Gift, MessageCircleMore, Megaphone, QrCode } from 'lucide-vue-next'
import type { TemplateModule } from '../types/types'

import useMedia from './media'
import useLocalStorage from './useLocalStorage'
import useObject from './object'

import type { TemplatableData, TemplateDetail } from '@/types/types'

const { getBase64 } = useMedia()
const { user, stores } = useLocalStorage()
const { removeNull } = useObject()

export default function useTemplate() {
  const router = useRouter()

  const getRewardTypes = async (): Promise<Record<string, number>> => {
    const { data } = await axios.get('/rewards/reward-types')
    return data.data.reduce((acc, curr) => {
      acc[curr.slug] = curr.id
      return acc
    }, {})
  }

  const applyTemplate = async (module: string, urlParams?: Record<string, unknown>) => {
    switch (module) {
      case 'loyalty':
        await applyLoyaltyTemplate(urlParams)
        break
      default:
        handleRedirectToCreatePage(module, urlParams)
    }
  }

  const updateMerchantTexts = async (templatableData) => {
    await axios.put(`merchants/${user.value.merchant.id}/texts/${user.value.languageId}`, {
      ...templatableData.text
    })
  }

  const fetchTemplateDetail = async <T = TemplateDetail['templatable']>(
    templateId: number | string,
    params?: Record<string, string>
  ): Promise<Omit<TemplateDetail, 'templatable'> & { templatable: T }> => {
    const { data } = await axios.get(`templates/${templateId}`, { params })

    return data.data
  }

  const getTemplate = async (templateId: string): Promise<TemplatableData> => {
    const { templatable } = await fetchTemplateDetail<TemplatableData>(templateId, { module: 'loyalty' })

    return templatable
  }

  const applyLoyaltyTemplate = async (urlParams?: Record<string, unknown>) => {
    if (urlParams?.templateId) {
      const [templatableData, rewardTypes] = await Promise.all([
        getTemplate(urlParams.templateId as string),
        getRewardTypes()
      ])

      const rewardPromises = templatableData.rewards.map(async (reward) => {
        const url = await getBase64(reward.pictureUrl)
        reward.pictureUrl = url
        reward.storeIds = stores.value.map((store) => store.id)
        reward.includedSegmentIds = reward.includedSegmentIds ? reward.includedSegmentIds : []
        reward.excludedSegmentIds = reward.excludedSegmentIds ? reward.excludedSegmentIds : []
        reward.rewardTypeId = rewardTypes[reward.rewardType.slug]
        delete reward.rewardType

        return axios.post(`/merchants/${user.value.merchant.id}/loyalty-program/rewards`, reward)
      })

      const updateMerchantConfigs = axios.put(`merchants/${user.value.merchant.id}/configs`, {
        data: {
          ...removeNull(templatableData.configs)
        }
      })

      await updateMerchantTexts(templatableData)

      const [coverA, coverB, coverC, coverD] = await Promise.all([
        getBase64(templatableData.medias.coverA),
        getBase64(templatableData.medias.coverB),
        getBase64(templatableData.medias.coverC),
        getBase64(templatableData.medias.coverD)
      ])

      const updateMerchantMedia = axios.put(`merchants/${user.value.merchant.id}/media`, {
        coverA,
        coverB,
        coverC,
        coverD,
        logo: null,
        logoMail: null
      })

      await Promise.all([Promise.all(rewardPromises), updateMerchantTexts, updateMerchantConfigs, updateMerchantMedia])
      window.location.reload()
    } else {
      router.push({
        name: 'LoyaltyEdit',
        query: {
          activeTab: 'rewards'
        }
      })
    }
  }

  const templatesByModule = {
    'survey-question': {
      name: 'survey-question',
      createPage: 'SurveyCreate',
      icon: Megaphone
    },
    contests: {
      name: 'contest-game',
      createPage: 'ContestCreate',
      icon: QrCode
    },
    'legacy-contests': {
      name: 'contest-game',
      createPage: 'LegacyContestCreate',
      icon: QrCode
    },
    campaign: {
      name: 'campaign',
      createPage: 'CampaignCreate',
      icon: MessageCircleMore
    },
    loyalty: {
      name: 'loyalty',
      createPage: 'LoyaltyEdit',
      icon: Gift
    }
  }
  const fetchTemplates = async (module: TemplateModule) => {
    const urlParams = { module: templatesByModule[module].name }
    const { data } = await axios.get('templates', { params: urlParams })
    return data.data
  }

  const filterTemplateBy = async (module: TemplateModule, filter: string) => {
    const templates = await fetchTemplates(module)
    const templatesWithFilter = templates.filter((template) => !!template[filter])
    const resultOfFilter = templatesWithFilter.reduce((a: TemplateModule, b: TemplateModule) => {
      return a[filter] > b[filter] ? a : b
    })
    return resultOfFilter
  }

  const handleRedirectToCreatePage = (module: string, urlParams?: Record<string, unknown>) => {
    const redirect = {
      name: templatesByModule[module].createPage
    } as Record<string, unknown>
    if (urlParams) {
      redirect.query = { ...urlParams }
    }

    router.push(redirect)
  }

  return {
    templatesByModule,
    fetchTemplates,
    fetchTemplateDetail,
    filterTemplateBy,
    applyTemplate
  }
}
